/**
 * Live staff chat (SR-14): subscribes to StaffChatMessage, StaffChatRead, StaffChatUnread and StaffChatThread on the
 * Notifications hub (`/api/notifications/NotificationsHub`) and writes the TanStack Query caches.
 *
 * Connection: this app has no shared NotificationsHub connection. Each feature that listens on it builds its own
 * through `createHubConnection` (`useNotificationCountRealtime`, `useInboxNewMessageNotify`), so with the staff chat
 * on a tab holds three. The staff chat opens exactly one, mounted once in the app shell and only while
 * `?x=staff-chat` is on. Sharing one connection across the three is a follow-up for all of them.
 *
 * After a reconnect the caches may have missed events, so every staff-chat query is refetched.
 */
import { useEffect } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/auth/auth-store';
import { NOTIFICATIONS_SIGNALR_HUB } from '@/notifications/lib/notification-constants';
import { createHubConnection } from '@/shared/realtime/signalr-connection';
import { staffChatKeys } from '../services/staff-chat-cache';
import { useStaffChatDock } from '../dock/dock-store';
import { STAFF_CHAT_EVENTS, applyStaffChatEvent, readStaffChatEvent } from './staff-chat-events';

export function useStaffChatRealtime(enabled: boolean): void {
  const userId = useAuthStore((s) => s.user?.userId);
  const qc = useQueryClient();

  // Synchronises with the Notifications SignalR hub (external system).
  useEffect(() => {
    if (!enabled || !userId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();
    const hub = createHubConnection(NOTIFICATIONS_SIGNALR_HUB, getToken);

    let disposed = false;
    // The dock's "Reconnecting..." strip follows the connection.
    const setConnection = useStaffChatDock.getState().setConnection;
    for (const name of Object.values(STAFF_CHAT_EVENTS)) {
      hub.on(name, (...args: unknown[]) => {
        const event = readStaffChatEvent(name, args);
        if (event) applyStaffChatEvent(qc, event, userId);
      });
    }
    hub.onreconnecting(() => setConnection('reconnecting'));
    hub.onreconnected(() => {
      setConnection('connected');
      void qc.invalidateQueries({ queryKey: staffChatKeys.all });
    });
    // A start that fails, or a connection the automatic reconnect gave up on, is started again after a pause
    // (5 s, doubling to 60 s). Whatever arrived meanwhile is read again once it is back.
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let delay = 5_000;
    let wasDown = false;
    const scheduleStart = () => {
      if (disposed || retryTimer) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        void startSafely();
      }, delay);
      delay = Math.min(delay * 2, 60_000);
    };
    hub.onclose(() => {
      if (disposed) return;
      wasDown = true;
      setConnection('reconnecting');
      scheduleStart();
    });

    const startSafely = async () => {
      try {
        if (hub.state === HubConnectionState.Disconnected) await hub.start();
        if (disposed) {
          if (hub.state !== HubConnectionState.Disconnected) await hub.stop();
          return;
        }
        setConnection('connected');
        delay = 5_000;
        if (wasDown) void qc.invalidateQueries({ queryKey: staffChatKeys.all });
        wasDown = false;
      } catch {
        // The reads and sends still work over HTTP meanwhile. StrictMode aborts are expected.
        if (disposed) return;
        wasDown = true;
        setConnection('reconnecting');
        scheduleStart();
      }
    };
    void startSafely();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      setConnection('connected');
      // Any state but Disconnected (Connecting, Reconnecting, Connected): a hub left reconnecting would live on and
      // keep writing to the cache after sign-out or the switch going off.
      if (hub.state !== HubConnectionState.Disconnected) void hub.stop().catch(() => {});
    };
  }, [enabled, userId, qc]);
}
